<template>
    <div class="column user-dashboard" :class="tierClass" :style="panelStyles">
            <!-- All the decorative layers — lighting, actors, scenery.
                 Its own clipped box rather than a pseudo-element on
                 .user-dashboard: that used to mean the whole panel
                 carried overflow:hidden, which also trapped every
                 tooltip and popover inside it against the panel's own
                 edges instead of letting them float over the page. -->
            <theme-scene :theme="theme" />
            <!-- The card is themed too, above legendary: its shape, its
                 material, and the architecture on top. `boxFrame` is a
                 backing plate rather than a border — see boxStyles(). -->
            <div
                class="user-profile-wrap"
                :class="{ 'has-header': !!themeHeader }"
                :style="boxWrapStyles">
                <div
                    v-if="themeHeader"
                    class="box-header"
                    :style="{
                        height: `${themeHeader.height}px`,
                        ...(themeHeader.inset && { left: themeHeader.inset, right: themeHeader.inset }),
                    }"
                    aria-hidden="true">
                    <span
                        v-if="themeHeader.rail"
                        class="box-header-rail"
                        :style="themeHeader.rail"></span>
                    <svg
                        v-if="themeHeader.viewBox"
                        :viewBox="themeHeader.viewBox"
                        :preserveAspectRatio="themeHeader.fit"
                        v-html="themeHeader.body"></svg>
                </div>
                <!-- Always present, styled only when the box is clipped:
                     a clip-path eats a CSS border, so the frame has to be
                     a plate behind the card in the same shape. -->
                <div class="box-frame" :style="boxFrameStyles">
            <div
                class="user-profile dashboard-item glass"
                :class="tierClass"
                :style="[overlayStyles, boxStyles]">
                <div class="box-material" :style="boxMaterialStyles"></div>
                <div class="user-info">
                    <img v-if="avatar" class="avatar" :src="avatar" :alt="username" />
                    <div v-else class="avatar avatar-placeholder"></div>
                    <div class="user-info-text">
                        <h1 class="user-username user-info-row" :style="themeFontStyles">
                            {{ username }}
                            <!-- The premium crown. Web profile only for now.
                                 The brand's own mark rather than a font
                                 icon or a text glyph — it carries its gold
                                 and its grain with it. -->
                            <img
                                v-if="isPremium"
                                class="premium-crown"
                                src="@/assets/images/premium/crown.svg"
                                alt="Gamebot Premium" />
                        </h1>
                        <!-- Fits as many pills as the row actually has
                             room for and rolls the rest into a +N; see
                             fitTags(). The title is never dropped. -->
                        <div ref="tagRow" class="user-info-tags">
                            <!-- The equipped title is the one filled pill;
                                 everything beside it is outlined, so the
                                 chosen one still reads as the chosen one. -->
                            <b-tag v-if="title" rounded :class="`title-tag is-${title.rarity}`">
                                {{ title.name }}
                            </b-tag>
                            <b-tag v-else rounded class="title-tag is-empty">No title yet</b-tag>

                            <b-tag
                                v-for="achievement in shownAchievements"
                                :key="achievement.id"
                                rounded
                                :class="`title-tag is-outlined is-${achievement.rarity}`">
                                {{ achievement.name }}
                            </b-tag>

                            <!-- Goes to the Stats tab rather than listing
                                 the hidden names on hover: the full set
                                 is what someone wants when they notice
                                 there are more, and a hover isn't
                                 reachable on touch. -->
                            <button
                                v-if="hiddenAchievementCount > 0"
                                type="button"
                                class="tag is-rounded title-tag is-outlined is-more"
                                :title="`View all ${achievementCount} achievements`"
                                @click="page = 'Stats'">
                                +{{ hiddenAchievementCount }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="column user-statistic">
                    <div class="game-circles user-info-row">
                        <b-tooltip
                            v-for="game of mostPlayed"
                            :key="game.id"
                            :label="`${game.name} — ${game.games} games`">
                            <div class="circle" :style="game.styles">{{ game.id }}</div>
                        </b-tooltip>
                        <span v-if="!mostPlayed.length" class="statistic-empty">No games yet</span>
                    </div>
                    <p class="statistic-label">Most Played</p>
                </div>
                <div class="column user-statistic">
                    <div class="user-info-row">
                        <p class="statistic-title">{{ totalWins }}</p>
                    </div>
                    <p class="statistic-label">Wins</p>
                </div>
            </div>
                </div><!-- /.box-frame -->
            </div><!-- /.user-profile-wrap -->


            <div class="user-status dashboard-item">
                <!-- Daily quests are personal progress, not a public
                     stat, and the read-only payload doesn't carry them. -->
                <div v-if="interactive" class="user-achievements dashboard-item glass" :class="tierClass" :style="overlayStyles">
                    <peek-slot :theme="theme" zone="quests" />
                    <div class="achievement-list">
                        <div v-for="quest in quests" :key="quest.id" class="achievement">
                            <!-- Name and progress together on the left, so
                                 "what am I doing / how far am I" reads as
                                 one phrase; the reward sits out on the
                                 right, and the bar carries the detail
                                 underneath. -->
                            <div class="achievement-info">
                                <p class="achievement-title">
                                    {{ quest.title }}
                                    <span v-if="quest.completed" class="achievement-done">✓</span>
                                    <span v-else class="achievement-progress-label">
                                        {{ quest.progress }}/{{ quest.target }}
                                    </span>
                                </p>
                                <p class="achievement-meta">
                                    <span class="achievement-reward">+{{ quest.xpReward }} XP</span>
                                </p>
                            </div>
                            <div class="achievement-progress">
                                <div
                                    class="achievement-progress-bar"
                                    :style="{
                                        width: quest.percent,
                                        backgroundColor: theme.colors.accent,
                                    }"></div>
                            </div>
                        </div>
                        <!-- Only claim there are no quests once we can
                             actually tell. Without the pool a player who
                             has three quests assigned was being told to go
                             play a game to get some. -->
                        <p v-if="!quests.length && questsResolved" class="statistic-empty">
                            Play a game to get your daily quests.
                        </p>
                        <p v-else-if="!quests.length" class="statistic-empty">
                            Loading your quests…
                        </p>
                    </div>
                    <!-- Reset timer is background information, not a
                         headline — a quiet tab along the bottom edge. -->
                    <div v-if="quests.length" class="achievement-timer">
                        Quests reset in <b>{{ questResetCountdown }}</b>
                    </div>
                </div>

                <div class="user-balance dashboard-item glass" :class="tierClass" :style="overlayStyles">
                    <peek-slot :theme="theme" zone="balance" />
                    <div class="user-balance-content">
                        <p class="user-balance-currency">
                            <img class="currency-icon" src="@/assets/images/currency/256w/credits.png" alt="Gamebot Credits" />
                            <b>Credits</b>
                        </p>
                        <p class="user-balance-amount">{{ dbInfo.balance || 0 }}</p>
                    </div>

                    <div class="user-balance-content">
                        <p class="user-balance-currency">
                            <img class="currency-icon" src="@/assets/images/currency/256w/gold.png" alt="Gamebot Gold" />
                            <b>Gold</b>
                        </p>
                        <p class="user-balance-amount">{{ dbInfo.goldBalance || 0 }}</p>
                    </div>
                </div>
            </div>

            <div class="xp-progress-bar dashboard-item glass" :class="tierClass" :style="overlayStyles">
                <peek-slot :theme="theme" zone="level" />
                <div
                    class="current-progress"
                    :style="{ width: levelProgressPercent, backgroundColor: theme.colors.secondary }">
                </div>
                <p class="xp-progress-label">
                    <b>Level {{ level }}</b>
                    <!-- displayExperience already ends in "XP", so the
                         word after it just said the same thing twice. -->
                    &nbsp;{{ displayExperience }}
                </p>
            </div>


            <!-- The reward track is the claim surface, and every card on
                 it is drawn from *your* claimable/locked state — which we
                 have no equivalent of for another user. Rendering it on
                 their profile would show their season as though nothing
                 in it had been claimed.

                 Profiles are signed-in only (router/profileGuard.js), so
                 `interactive` is now a straight own-vs-other question. It
                 was already that for a signed-out visitor at a named
                 profile — the route param stands in as `userID`, so they
                 read as viewing someone else and the track stayed hidden,
                 which is the confusion the gate fixes rather than this
                 flag. -->
            <div v-if="interactive" class="user-experience dashboard-item">
                <div class="rewards-header">
                    <!-- Just "Rewards" — the season pill beside it
                         already says which season these belong to. -->
                    <p class="rewards-title" :style="themeFontStyles">Rewards</p>
                    <!-- The season belongs here rather than floating in the
                         profile card's corner: this is the one place on the
                         page where which season it is changes what you're
                         looking at. Hidden until it resolves — an empty
                         pill is worse than none. -->
                    <span v-if="seasonName" class="season-pill" :style="seasonPillStyles">
                        <b-icon :icon="seasonIcon" size="is-small" />
                        {{ seasonName }}
                    </span>
                    <!-- Only offered once the current level has actually
                         scrolled out of sight — a button that does nothing
                         visible is worse than no button.

                         Named for where it takes you. "Reset" said nothing
                         about *what* it resets, and next to a Claim all
                         button on a page full of progress it reads like it
                         might undo something.

                         Hidden with visibility rather than v-if/v-show so
                         it keeps occupying its space: removing it from
                         layout made the whole header (and the track under
                         it) jump sideways every time it appeared. -->
                    <b-button
                        class="rewards-recentre"
                        :class="{ 'is-hidden-until-needed': !currentLevelOffscreen }"
                        :style="themeTextStyles"
                        size="is-small"
                        icon-left="arrow-circle-right"
                        icon-pack="fas"
                        :tabindex="currentLevelOffscreen ? 0 : -1"
                        :aria-hidden="!currentLevelOffscreen"
                        :title="`Scroll the reward track back to level ${level}, where you are now`"
                        @click="centreTrackOnCurrentLevel(true)">
                        Back to level {{ level }}
                    </b-button>
                    <b-button
                        v-if="claimableCount > 0"
                        class="themed-claim"
                        size="is-small"
                        :style="claimButtonStyles"
                        :loading="claiming && claimingLevel === null"
                        @click="claimAll">
                        Claim all ({{ claimableCount }})
                    </b-button>
                </div>
                <!-- Horizontal scroll + snap rather than a carousel
                     dependency: this gives native momentum swiping on
                     touch, keyboard scrolling on desktop, and costs
                     nothing in bundle size. -->
                <div ref="rewardTrack" class="rewards-container">
                    <div
                        v-for="entry in rewardTrack"
                        :key="entry.level"
                        class="reward-container"
                        :class="[entry.state, {
                            'is-current': entry.level === level,
                            'is-finale': entry.isFinale,
                        }]">
                        <!-- The current-level badge is themed, so it stays
                             legible on whatever background is applied
                             rather than assuming a light one. -->
                        <!-- A locked card must NOT take the theme overlay:
                             an inline style beats the .locked class, which is
                             what kept the dark scrim from ever showing. -->
                        <div
                            class="reward glass"
                            :class="{ 'is-clickable': entry.state === 'locked' || claimByCard(entry) }"
                            :style="entry.state === 'locked' ? null : overlayStyles"
                            @click="onCardClick(entry)">
                            <!-- A locked card still shows what's inside
                                 it. Hiding the reward behind a padlock
                                 removes the only reason to subscribe —
                                 seeing what has piled up unclaimed is
                                 the whole pitch. -->
                            <img
                                v-if="entry.state === 'locked'"
                                class="reward-crown-badge"
                                src="@/assets/images/premium/crown-white.svg"
                                alt="" />
                            <!-- The finale grants two things (the free
                                 credits and the Mythical item), so it
                                 shows both rather than hiding half of
                                 the season's payoff in a tooltip. -->
                            <template v-if="entry.isFinale">
                                <span class="finale-sparkles" aria-hidden="true"></span>
                                <p class="finale-label">Season finale</p>
                                <div class="finale-rewards">
                                    <template v-for="(reward, i) in entry.rewards">
                                        <span
                                            v-if="i > 0"
                                            :key="`sep-${i}`"
                                            class="finale-separator"
                                            aria-hidden="true"></span>
                                        <div
                                            :key="i"
                                            class="finale-reward"
                                            :class="{ 'is-premium-locked': reward.premium && !isPremium }">
                                            <span class="finale-reward-icon">
                                                <rarity-gem
                                                    v-if="reward.gemRarity"
                                                    class="reward-icon is-large"
                                                    :rarity="reward.gemRarity" />
                                                <img
                                                    v-else-if="reward.icon"
                                                    class="reward-icon is-large"
                                                    :src="reward.icon"
                                                    :alt="reward.alt" />
                                            </span>
                                            <p class="reward-value">{{ reward.short }}</p>
                                            <!-- The Mythical item is
                                                 premium-only. Showing the
                                                 finale's two rewards side
                                                 by side without saying so
                                                 promises a free player a
                                                 prize they won't get.
                                                 The row is always present
                                                 (empty for the credits)
                                                 so both labels sit on the
                                                 same line. -->
                                            <p class="finale-premium-tag">
                                                <template v-if="reward.premium">
                                                    <img
                                                        v-if="!isPremium"
                                                        class="finale-crown"
                                                        src="@/assets/images/premium/crown.svg"
                                                        alt="" />
                                                    Premium
                                                </template>
                                            </p>
                                        </div>
                                    </template>
                                </div>
                            </template>
                            <template v-else>
                                <rarity-gem
                                    v-if="entry.gemRarity"
                                    class="reward-icon"
                                    :rarity="entry.gemRarity" />
                                <img
                                    v-else-if="entry.icon"
                                    class="reward-icon"
                                    :src="entry.icon"
                                    :alt="entry.alt" />
                                <p class="reward-value">{{ entry.short }}</p>
                            </template>
                            <b-button
                                v-if="entry.state === 'claimable' && !isNarrow"
                                class="reward-claim themed-claim"
                                :style="claimButtonStyles"
                                :loading="claimingLevel === entry.level"
                                :disabled="claiming && claimingLevel !== entry.level"
                                @click="claim(entry.level)">
                                Claim!
                            </b-button>
                            <span
                                v-else-if="entry.state === 'claimed' && !isNarrow"
                                class="reward-claimed">✓</span>
                            <span v-else-if="entry.state === 'locked'" class="reward-lock">Premium</span>
                        </div>
                        <!-- On a phone the level number gives up its slot
                             to the card's state: at 4.75rem there is no
                             room inside for a button big enough to hit,
                             nor for a tick beside the reward it's ticking
                             off, and the level is the one thing here you
                             can work out from position alone. -->
                        <p
                            v-if="claimByCard(entry)"
                            class="reward-label is-claim-mark"
                            :class="{ 'is-working': claimingLevel === entry.level }">
                            <b-icon icon="gift" size="is-small" />
                        </p>
                        <p
                            v-else-if="isNarrow && entry.state === 'claimed'"
                            class="reward-label is-claimed-mark">
                            <b-icon icon="check" size="is-small" />
                        </p>
                        <p
                            v-else
                            class="reward-label"
                            :style="entry.level === level ? currentLevelBadgeStyles : null">
                            {{ entry.level }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- Managing an active subscription moved into the account
                 dropdown in the nav (next to the name everywhere, not
                 just here) — this footer no longer needs a right side. -->
            <div class="user-footer dashboard-item glass" :class="tierClass" :style="overlayStyles">
                <peek-slot :theme="theme" zone="footer" />
                <!-- Carries the theme's display face, like the username and
                     the Rewards heading. It was the one piece of chrome on
                     the card still rendering in the site's UI font, which
                     read as though it belonged to the page rather than to
                     the profile. -->
                <span class="user-footer-caption" :style="themeFontStyles">
                    {{ seasonName }} / Gamebot for Discord
                </span>

                <!-- Only when there's a real id to share. `/profile/me`
                     resolves to whoever opens it, so a copied `me` link
                     would show the reader their own profile — the same trap
                     the bot's links were changed to avoid. -->
                <button
                    v-if="dbInfo.userID"
                    type="button"
                    class="share-profile"
                    :class="{ 'is-copied': copiedRecently }"
                    :aria-label="copiedRecently ? 'Link copied' : 'Copy link to this profile'"
                    @click="copyProfileLink">
                    <b-icon :icon="copiedRecently ? 'check' : 'link'" size="is-small" />
                    <span>{{ copiedRecently ? 'Copied' : 'Share' }}</span>
                </button>
            </div>

            <!-- The way out to the sales page. Sits after the track, so
                 it lands right where a free player has just scrolled past
                 everything they can't claim. -->
            <!-- `interactive`, because this is addressed to *you* — down to
                 counting your own unclaimed rewards in its headline. On
                 someone else's profile it read as a statement about them. -->
            <router-link v-if="interactive && !isPremium" class="premium-banner dashboard-item" to="/premium">
                <div class="premium-banner-text">
                    <p class="premium-banner-title">
                        <template v-if="lockedCount > 0">
                            {{ lockedCount }} {{ lockedCount === 1 ? 'reward is' : 'rewards are' }} waiting for you
                        </template>
                        <template v-else>Level faster. Look better.</template>
                    </p>
                    <p class="premium-banner-subtitle">
                        Gamebot Premium{{ premiumPriceLabel ? ` — ${premiumPriceLabel}` : '' }}
                    </p>
                </div>
                <span class="premium-banner-cta">
                    See what you get
                    <b-icon icon="angle-right" size="is-small" />
                </span>
            </router-link>

    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import { XP_LEVELS, LEVEL_REWARDS } from '../../assets/js/rewards.js'
import { colorsForGame, stylesForSeason } from '../../globals/games.js'
import {
    debounce,
    joinQuests,
    levelBounds,
    levelProgress,
    mostPlayed,
    questCountdown,
    rewardState,
    topAchievements,
    totalWins } from '../../util/profile.js'
import {
    accentStyles,
    boxFrameStyles,
    boxMaterialStyles,
    boxStyles,
    boxWrapStyles,
    controlShapeStyles,
    headerStyles,
    tierClass } from '../../util/themeStyles.js'
import {
    describeReward,
    gemRarity,
    rewardIcon,
    shortReward } from '../../util/rewardDisplay.js'
import themedPanel from '../../mixins/themedPanel.js'

import PeekSlot from './PeekSlot.vue'
import RarityGem from './RarityGem.vue'
import ThemeScene from './ThemeScene.vue'


/**
 * How many achievements are candidates for the row beside the title. How many
 * actually render is decided at runtime by fitTags(), which measures the real
 * row — this is only a ceiling so a player with fifty achievements doesn't
 * render fifty pills just to hide most of them.
 */
const MAX_SECONDARY_ACHIEVEMENTS = 4

/**
 * `/profile/:id` — the landing panel: who this is, what they have, and the
 * season's reward track.
 *
 * ── What it doesn't own ──────────────────────────────────────────────────
 *
 * Two modals belong to `Profile.vue` instead. The celebration reads
 * `lastClaim` from the store, so it needs nothing from here at all; the
 * premium-details modal does, and gets it through `show-locked`.
 *
 * They sit in the shell because a modal rendered inside a routed panel is
 * torn down the moment the route changes, and because Buefy's `.modal` is
 * `position: fixed` against the viewport — nesting it inside a panel that
 * clips its own overflow is exactly how it ended up trapped in the profile
 * once already.
 */
export default {
    name: 'ProfileOverview',
    components: { PeekSlot, RarityGem, ThemeScene },
    mixins: [themedPanel],
    props: {
        /** The user this panel is about — see the shell's `dbInfo`. */
        dbInfo: { type: Object, required: true },
        /**
         * False on someone else's profile, where the quest card, the upsell
         * and the whole reward track are hidden: the first isn't public, and
         * the rest are drawn from *your* claim state.
         */
        interactive: { type: Boolean, default: true },
    },
    data() {
        return {
            // Backs the quest countdown. Ticked once a minute rather than
            // computed from Date.now() inline, which Vue can't treat as a
            // reactive dependency and so would never re-render.
            now: Date.now(),
            nowTimer: null,
            // Swaps the share control to its confirmation for a moment.
            copiedRecently: false,
            copiedTimer: null,
            // Whether the current-level card has scrolled out of the track's
            // visible range. Updated from a scroll listener rather than
            // computed, since scroll position isn't reactive state.
            currentLevelOffscreen: false,
            // Debounced because fitTags() forces a layout pass per candidate
            // pill, and dragging a window edge fires resize dozens of times a
            // second. Held on the instance so it can be removed and cancelled.
            onResize: null,
            // Phone layout, tracked rather than assumed: the card becomes the
            // tap target and the level number becomes the claim mark, and
            // neither is something CSS alone can wire up.
            isNarrow: false,
            onNarrowChange: null,
            narrowQuery: null,
            // How many secondary achievement pills the row currently has room
            // for. null means "not measured yet" — render them all so
            // fitTags() has something to measure.
            visibleAchievementCount: null,
            // Where the reward track was left. See deactivated().
            trackScrollLeft: null,
        }
    },
    computed: {
        ...mapState('profile', ['premium', 'claiming', 'claimingLevel', 'price']),
        ...mapGetters('profile', ['lockedCount', 'achievementsById']),

        /**
         * Whether the *subject* subscribes — the crown beside their name.
         *
         * Deliberately not `|| this.premium`, which is the signed-in viewer's
         * own status: on a stranger's profile that put a crown on their name
         * because *you* subscribe. On your own profile the shell folds your
         * status into `dbInfo`, so the two agree there anyway.
         */
        isPremium() { return this.dbInfo.premium === true },

        // Both ride along on `dbInfo` — the shell normalises them onto it so
        // this panel never has to know whose profile it is rendering.
        username() { return this.dbInfo.username || 'Player' },
        avatar() { return this.dbInfo.avatar || null },

        // Card geometry and the architecture on top of it — Overview's alone,
        // so they're imported rather than living in the shared mixin.
        tierClass() { return tierClass(this.theme) },
        boxStyles() { return boxStyles(this.theme) },
        boxFrameStyles() { return boxFrameStyles(this.theme) },
        boxMaterialStyles() { return boxMaterialStyles(this.theme) },
        boxWrapStyles() { return boxWrapStyles(this.theme) },
        themeHeader() { return headerStyles(this.theme) },
        controlShapeStyles() { return controlShapeStyles(this.theme) },
        currentLevelBadgeStyles() { return accentStyles(this.theme) },

        /**
         * The claim button, in the theme's accent and the theme's shape.
         */
        claimButtonStyles() {
            return { ...accentStyles(this.theme), ...this.controlShapeStyles }
        },

        /**
         * The season chip keeps its *seasonal* colour — that's the one thing
         * it's telling you, and a theme shouldn't be able to repaint Summer
         * to look like Winter — but takes the theme's shape, so it still
         * belongs to the box it sits on.
         */
        seasonPillStyles() {
            const styles = stylesForSeason(this.season && this.season.season)
            return {
                backgroundColor: styles.background,
                color: styles.text,
                ...this.controlShapeStyles,
            }
        },


        level() { return this.dbInfo.level || 0 },
        xp() { return this.dbInfo.xp || 0 },

        /**
         * XP into the current level, over what the next level costs — not raw
         * lifetime XP, which would show a bar that never resets.
         *
         * XP_LEVELS is a flat array where index i holds the threshold for
         * level i+1, so the floor for the current level is XP_LEVELS[level-1]
         * and the ceiling is XP_LEVELS[level].
         */
        levelBounds() { return levelBounds(this.level, XP_LEVELS) },
        displayExperience() {
            const { floor, ceiling } = this.levelBounds
            // Max level: there is no next threshold to count toward.
            if (ceiling === null) return `${this.xp.toLocaleString()} XP`
            return `${(this.xp - floor).toLocaleString()} / ${(ceiling - floor).toLocaleString()} XP`
        },
        levelProgressPercent() {
            return `${(levelProgress(this.xp, this.level, XP_LEVELS) * 100).toFixed(1)}%`
        },

        stats() { return this.dbInfo.stats || {} },
        gameNames() {
            return this.$store.state.profile.games.reduce((map, game) => {
                map[game.id] = game.name
                return map
            }, {})
        },
        mostPlayed() {
            return mostPlayed(this.stats, this.gameNames).map(game => {
                const colors = colorsForGame(game.id)
                return { ...game, styles: { backgroundColor: colors.background, color: colors.text } }
            })
        },
        totalWins() { return totalWins(this.stats) },

        /** The pills actually rendered, after fitTags() has trimmed to fit. */
        shownAchievements() {
            if (this.visibleAchievementCount === null) return this.topAchievements
            return this.topAchievements.slice(0, this.visibleAchievementCount)
        },

        hiddenAchievementCount() {
            return this.topAchievements.length - this.shownAchievements.length
        },

        /** How many achievements the user has unlocked in total. */
        achievementCount() {
            return (this.dbInfo.achievements || []).length
        },

        /**
         * The rest of the user's best achievements, filling the space beside
         * the equipped title. Rarest first — a player's headline is what they
         * earned that was hardest to earn, not what they happened to unlock
         * most recently.
         */
        topAchievements() {
            return topAchievements({
                unlocked: this.dbInfo.achievements,
                catalog: this.achievementsById,
                equippedTitle: this.dbInfo.title,
                limit: MAX_SECONDARY_ACHIEVEMENTS,
            })
        },

        /**
         * The one title the user has equipped, resolved against the
         * achievement catalog. The pill is styled by the achievement's rarity
         * tier, so a Mythical title reads differently from a Common one.
         */
        title() {
            const equipped = this.dbInfo.title
            if (!equipped) return null
            const meta = this.achievementsById[equipped]
            return meta ? { name: meta.name, rarity: meta.rarity } : null
        },

        /**
         * The live season name, e.g. "Summer 2026". Served from
         * /api/season, which computes it from the date — the copy stored in
         * the database has been stale since 2024.
         */
        season() { return this.$store.state.profile.season },
        seasonName() {
            const season = this.season
            return season ? season.name : ''
        },
        seasonIcon() { return stylesForSeason(this.season && this.season.season).icon },
        /**
         * The user's three assigned quests, joined against the pool for the
         * bits their document doesn't store (description, target, reward).
         */
        quests() {
            return joinQuests(this.dbInfo.quests, this.$store.state.profile.questPool)
        },

        /**
         * Whether we know enough to say a player has no quests. The pool has
         * to have arrived first — a failed or pending /api/quests otherwise
         * looks exactly like an empty quest list.
         */
        questsResolved() {
            return this.$store.state.profile.questPoolLoaded
        },

        /** Time until the daily quest reset, counted from the last assignment. */
        questResetCountdown() {
            return questCountdown(this.dbInfo.lastQuest, this.$store.state.profile.questResetMs, this.now)
        },

        premiumPriceLabel() {
            if (!this.price) return null
            const amount = (this.price.unitAmount / 100).toFixed(2)
            const currency = (this.price.currency || 'usd').toUpperCase()
            const symbol = currency === 'USD' ? '$' : ''
            return `${symbol}${amount}${symbol ? '' : ` ${currency}`}/${this.price.interval || 'month'}`
        },

        claimableCount() {
            return this.$store.getters['profile/claimableRewards'].length
        },

        /**
         * What was just claimed, described for the celebration.
         *
         * Normalised here rather than inside the celebration because the
         * reward → icon/gem/wording mapping already lives on this page; a
         * second copy in the component is a second thing to keep in step.
         */
        celebrationRewards() {
            if (!this.lastClaim) return []
            return (this.lastClaim.rewards || []).map(reward => ({
                kind: this.rewardKind(reward),
                icon: this.rewardIcon(reward),
                gemRarity: this.gemRarity(reward),
                amount: this.rewardAmount(reward),
                unit: this.rewardUnit(reward),
                label: this.describeReward(reward),
            }))
        },

        /**
         * The whole season's reward track. How many are visible at once is a
         * layout question, so the card width in `.rewards-container` owns it
         * (five) rather than a constant here — the rest scroll, and mounted()
         * scrolls the player's current level into the middle, so the default
         * view is still "where am I", but the full season is reachable.
         */
        rewardTrack() {
            const profile = this.$store.state.profile
            const claimableLevels = new Set(profile.claimable.map(e => e.level))
            const lockedLevels = new Set(profile.locked.map(e => e.level))

            const track = []
            for (let lvl = 1; lvl <= XP_LEVELS.length; lvl++) {
                const rewards = LEVEL_REWARDS[lvl] || []
                if (rewards.length === 0) continue

                // A level can grant more than one reward (level 50 grants two).
                // The card shows the first; the rest are in the tooltip text.
                const primary = rewards[0]
                const premiumOnly = rewards.every(reward => reward.premium)
                // The last level of the season is the chase goal — it grants
                // both the free credit payout and a premium-only Mythical
                // item, and gets a card that says so.
                const isFinale = lvl === XP_LEVELS.length

                const state = rewardState({
                    level: lvl,
                    userLevel: this.level,
                    claimableLevels,
                    lockedLevels,
                    premiumOnly,
                    isPremium: this.isPremium,
                    rewardsLoaded: profile.rewardsLoaded,
                })

                track.push({
                    level: lvl,
                    state,
                    isFinale,
                    // The finale shows everything it grants; every other card
                    // shows its primary reward and puts the rest in a tooltip.
                    rewards: isFinale
                        ? rewards.map(reward => ({
                            gemRarity: this.gemRarity(reward),
                            icon: this.rewardIcon(reward),
                            short: this.shortReward(reward),
                            alt: reward.type,
                            premium: reward.premium === true,
                        }))
                        : [],
                    icon: this.rewardIcon(primary),
                    // Item rewards draw a gem in their rarity's colours rather
                    // than a flat image — see RarityGem.
                    gemRarity: this.gemRarity(primary),
                    short: this.shortReward(primary),
                    alt: primary.type,
                })
            }
            return track
        },
    },
    methods: {
        /**
         * Puts a link to this profile on the clipboard.
         *
         * Built from the id rather than `window.location.href`, which can be
         * sitting on `/profile/me` or carry a `?ref=` from wherever the
         * reader arrived — neither is what you'd want to hand someone.
         *
         * The async Clipboard API needs a secure context and a permission
         * that can be refused, so the old `execCommand` route stays as a
         * fallback. Failing silently would be the worst outcome: the button
         * would say "Copied" over an empty clipboard.
         */
        async copyProfileLink() {
            // `window.location.origin`, deliberately, and only here. The web
            // build has no absolute site origin to reach for — vue-cli
            // reserves BASE_URL for publicPath and rewrites it to "/" — so
            // the running host is the only truthful answer, and for a link
            // being copied *out of* the page you are on, it is the right one.
            // The bot has the same question and a different answer: it isn't
            // running in a browser, so it reads BASE_URL through siteURL().
            const url = `${window.location.origin}/profile/${this.dbInfo.userID}`
            let copied = false

            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(url)
                    copied = true
                }
            } catch (err) {
                // Refused or unavailable — fall through and try the old way.
                copied = false
            }

            if (!copied) copied = this.copyViaTextarea(url)
            if (!copied) {
                this.$buefy.snackbar.open({
                    message: 'Could not copy the link. You can copy it from the address bar.',
                    type: 'is-warning',
                })
                return
            }

            this.copiedRecently = true
            if (this.copiedTimer) clearTimeout(this.copiedTimer)
            this.copiedTimer = setTimeout(() => { this.copiedRecently = false }, 2000)
        },

        /**
         * The pre-Clipboard-API way. Off-screen rather than `display: none`,
         * which cannot be selected, and readonly so focusing it doesn't open
         * a keyboard on a phone.
         *
         * @param {string} text
         * @returns {boolean} Whether the copy actually happened.
         */
        copyViaTextarea(text) {
            try {
                const field = document.createElement('textarea')
                field.value = text
                field.setAttribute('readonly', '')
                field.style.position = 'fixed'
                field.style.left = '-9999px'
                document.body.appendChild(field)
                field.select()
                const ok = document.execCommand('copy')
                document.body.removeChild(field)
                return ok
            } catch (err) {
                return false
            }
        },

        // Shared with the celebration the shell renders — see
        // util/rewardDisplay.js. Exposed as methods because the template
        // calls them per card.
        describeReward,
        rewardIcon,
        gemRarity,
        shortReward,

        /**
         * Scrolls the current level to the middle of the visible five.
         * `scrollIntoView` is deliberately not used — it would also scroll the
         * page vertically to reach the track.
         * @param {boolean} smooth Animate, for a user-initiated re-centre.
         */
        centreTrackOnCurrentLevel(smooth = false) {
            const track = this.$refs.rewardTrack
            if (!track) return

            const card = track.querySelector('.reward-container.is-current')
            if (!card) return

            const left = card.offsetLeft + (card.offsetWidth / 2) - (track.clientWidth / 2)
            if (smooth && track.scrollTo) track.scrollTo({ left, behavior: 'smooth' })
            else track.scrollLeft = left

            this.updateCurrentLevelVisibility()
        },

        /**
         * Scroll handler.
         *
         * Deliberately not throttled through requestAnimationFrame. rAF is
         * paused in a backgrounded tab, so a queued frame can simply never
         * run and the button then reflects a scroll position from minutes
         * ago. The work here is four reads of already-computed layout values
         * and one boolean assignment — cheap enough that the throttle bought
         * nothing but a way to be wrong.
         */
        onTrackScroll() {
            this.updateCurrentLevelVisibility()
        },

        /** Tracks whether the current-level card is still in view. */
        updateCurrentLevelVisibility() {
            const track = this.$refs.rewardTrack
            if (!track) {
                this.currentLevelOffscreen = false
                return
            }

            const card = track.querySelector('.reward-container.is-current')
            if (!card) {
                this.currentLevelOffscreen = false
                return
            }

            // Compared in the track's own scroll space rather than with
            // getBoundingClientRect, so this is unaffected by where the page
            // happens to be scrolled vertically.
            const cardStart = card.offsetLeft
            const cardEnd = cardStart + card.offsetWidth
            const viewStart = track.scrollLeft
            const viewEnd = viewStart + track.clientWidth

            this.currentLevelOffscreen = cardEnd < viewStart || cardStart > viewEnd
        },

        /**
         * Trims the achievement pills to what the row can actually show on one
         * line, rolling the remainder into a "+N".
         *
         * Measured rather than assumed: how many fit depends on the viewport,
         * the theme's font, and how long the achievement names happen to be —
         * a fixed count is wrong at some width for some player. It works by
         * rendering everything, checking whether any pill has wrapped onto a
         * second row (a larger offsetTop than the first), and dropping one
         * from the end until nothing wraps. The "+N" badge is part of the row
         * while this runs, so it's included in what has to fit.
         */
        async fitTags() {
            const row = this.$refs.tagRow
            if (!row) return

            // Start from everything visible so there's something to measure.
            this.visibleAchievementCount = null
            await this.$nextTick()

            // Measures the pills themselves, not row.children — the "+N"
            // badge is wrapped in a b-tooltip, and that wrapper's box doesn't
            // line up with a bare tag's. Comparing wrapper to tag registered a
            // wrap that wasn't there and trimmed the row down to nothing.
            const wraps = () => {
                const pills = [...row.querySelectorAll('.tag')]
                if (pills.length < 2) return false
                const firstTop = pills[0].getBoundingClientRect().top
                // A few pixels of tolerance for sub-pixel layout differences.
                return pills.some(pill => pill.getBoundingClientRect().top > firstTop + 4)
            }

            if (!wraps()) return

            // Drop from the end until it fits. Bounded by the candidate count,
            // so this can't spin; the title pill is never dropped.
            for (let count = this.topAchievements.length - 1; count >= 0; count--) {
                this.visibleAchievementCount = count
                await this.$nextTick()
                if (!wraps()) return
            }
        },

        /**
         * Explains a premium-locked reward rather than leaving a dead card.
         *
         * The modal itself belongs to the shell — see the note on this
         * component. All this does is name the reward it should describe.
         */
        openPremiumDetails(entry) {
            this.$emit('show-locked', entry)
        },

        claim(level) { return this.$store.dispatch('profile/claim', level) },

        /** Whether this card is the thing you tap to claim (phones only). */
        claimByCard(entry) {
            return this.isNarrow && entry.state === 'claimable'
        },

        /**
         * The card's click. Locked cards open the upsell everywhere; a
         * claimable card only claims where the button isn't rendered, so a
         * desktop click can't fire the same claim the button already owns.
         */
        onCardClick(entry) {
            if (entry.state === 'locked') return this.openPremiumDetails(entry)
            if (this.claimByCard(entry) && !this.claiming) return this.claim(entry.level)
        },

        claimAll() { return this.$store.dispatch('profile/claimAll') },
    },
    watch: {
        // The pills change when the catalog arrives, when achievements load,
        // and when a title is equipped — each needs a fresh measurement.
        topAchievements() { this.$nextTick(this.fitTags) },
        title() { this.$nextTick(this.fitTags) },

        /**
         * Centre the track on the player's level whenever it becomes known.
         *
         * mounted() also calls this, but only after its own fetches resolve —
         * so a level arriving by any other route (a claim refetching dbInfo,
         * a slow first load, an already-warm store on re-entry) left the track
         * sitting at level 1. Reacting to the value covers all of them.
         */
        level(current, previous) {
            // Not on every level-up mid-session: that would yank the track
            // away from wherever the player had scrolled to.
            if (previous === 0 && current > 0) this.$nextTick(this.centreTrackOnCurrentLevel)
        },
    },
    /**
     * Puts the reward track back where it was.
     *
     * `keep-alive` keeps this component alive across a trip to the Stats tab,
     * but it does *not* keep the track's scroll position: deactivating
     * detaches the element from the document, and a detached element's
     * scrollLeft resets to 0. The instance survives and so does the DOM node
     * — only the offset is lost, silently, which reads as the track jumping
     * back to level 1 every time you glance at your stats.
     *
     * Captured in `beforeRouteLeave`, which is the last moment the element
     * is still in the document. A `deactivated` hook is too late — it runs
     * *after* the detach, so it reads a zero every time — and the scroll
     * listener is too unreliable, since a programmatic scroll doesn't
     * dependably deliver an event before navigation tears the page down.
     */
    beforeRouteLeave(to, from, next) {
        const track = this.$refs.rewardTrack
        if (track) this.trackScrollLeft = track.scrollLeft
        next()
    },
    activated() {
        this.$nextTick(() => {
            const track = this.$refs.rewardTrack
            if (!track) return
            if (this.trackScrollLeft === null) this.centreTrackOnCurrentLevel()
            else track.scrollLeft = this.trackScrollLeft
            this.updateCurrentLevelVisibility()
        })
    },
    beforeDestroy() {
        if (this.nowTimer) clearInterval(this.nowTimer)
        if (this.copiedTimer) clearTimeout(this.copiedTimer)
        if (this.narrowQuery && this.onNarrowChange) {
            if (this.narrowQuery.removeEventListener) this.narrowQuery.removeEventListener('change', this.onNarrowChange)
            else this.narrowQuery.removeListener(this.onNarrowChange)
        }
        if (this.onResize) {
            window.removeEventListener('resize', this.onResize)
            // A pending call would otherwise fire against a torn-down view.
            this.onResize.cancel()
        }
        const track = this.$refs.rewardTrack
        if (track) track.removeEventListener('scroll', this.onTrackScroll)
    },
    mounted() {
        this.nowTimer = setInterval(() => { this.now = Date.now() }, 60000)

        // Matches Bulma's `mobile` (<=768px), which is the same breakpoint the
        // cards shrink to 4.75rem at — the reason the in-card button stops
        // being a sensible tap target in the first place.
        const narrow = window.matchMedia('(max-width: 768px)')
        this.narrowQuery = narrow
        this.isNarrow = narrow.matches
        this.onNarrowChange = () => { this.isNarrow = narrow.matches }

        // addListener, not addEventListener: Safari only gained the modern
        // form in 14, and the deprecated one still works everywhere.
        if (narrow.addEventListener) narrow.addEventListener('change', this.onNarrowChange)
        else narrow.addListener(this.onNarrowChange)

        // Re-read on resize as well. `change` is the correct signal and fires
        // in a real browser, but it is not dependable in every embedded or
        // automated one, and a stale flag here is silent: a desktop keeps
        // rendering the phone affordance — no in-card button, and a card that
        // claims on click — with nothing to indicate why.
        this.onResize = debounce(() => {
            this.fitTags()
            this.onNarrowChange()
        }, 150)
        window.addEventListener('resize', this.onResize, { passive: true })

        this.$nextTick(() => {
            const track = this.$refs.rewardTrack
            if (track) track.addEventListener('scroll', this.onTrackScroll, { passive: true })
            // The level is usually already known by the time this panel
            // mounts — the shell fetches before rendering a route — so centre
            // straight away rather than waiting for the watcher, which only
            // fires on a change. Coming *back* to the tab is activated()'s
            // job, which restores where you left it instead.
            this.centreTrackOnCurrentLevel()
        })
    },
}
</script>
